import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy load views
const LoginView = () => import('@/views/LoginView.vue')
const LibraryView = () => import('@/views/LibraryView.vue')
const MatchDetailView = () => import('@/views/MatchDetailView.vue')
const CaptureView = () => import('@/views/CaptureView.vue')
const AllTimeStatsView = () => import('@/views/AllTimeStatsView.vue')
const StatLeadersView = () => import('@/views/StatLeadersView.vue')
const FraudWatchView = () => import('@/views/FraudWatchView.vue')
const H2HView = () => import('@/views/H2HView.vue')
const PlayerStatsView = () => import('@/views/PlayerStatsView.vue')
const CollectionPublicView = () => import('@/views/CollectionPublicView.vue')
const WatchView = () => import('@/views/WatchView.vue')
const LiveStreamView = () => import('@/views/LiveStreamView.vue')
const AdminView = () => import('@/views/AdminView.vue')
const AliasManagerView = () => import('@/views/AliasManagerView.vue')

const routes: RouteRecordRaw[] = [
  // Public routes
  {
    path: '/CvCJbECg53A',
    name: 'login',
    component: LoginView,
    meta: { public: true, hideNav: true }
  },
  {
    path: '/c',
    name: 'collections',
    component: CollectionPublicView,
    meta: { public: true, hideNav: true }
  },
  {
    path: '/c/:slug',
    name: 'collection-detail',
    component: CollectionPublicView,
    meta: { public: true, hideNav: true }
  },
  {
    path: '/gallery',
    redirect: '/c'
  },
  {
    path: '/gallery/:slug',
    redirect: to => `/c/${to.params.slug}`
  },
  {
    path: '/watch/:matchId',
    name: 'watch',
    component: WatchView,
    meta: { public: true, hideNav: true }
  },
  {
    path: '/live/:streamToken',
    name: 'live-stream',
    component: LiveStreamView,
    meta: { public: true, hideNav: true }
  },

  // Protected routes
  {
    path: '/library',
    name: 'library',
    component: LibraryView,
    meta: { requiresAuth: true }
  },
  {
    path: '/match/:id',
    name: 'match-detail',
    component: MatchDetailView,
    meta: { requiresAuth: true }
  },
  {
    path: '/capture',
    name: 'capture',
    component: CaptureView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats',
    name: 'all-time-stats',
    component: AllTimeStatsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats/3v3',
    name: 'leaderboard-3v3',
    component: AllTimeStatsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats/2v2',
    name: 'leaderboard-2v2',
    component: AllTimeStatsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats/1v1',
    name: 'leaderboard-1v1',
    component: AllTimeStatsView,
    meta: { requiresAuth: true }
  },

  {
    path: '/stats/rating-history',
    redirect: '/stats'
  },
  {
    path: '/stats/stat-leaders',
    name: 'stat-leaders',
    component: StatLeadersView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats/stat-leaders/3v3',
    name: 'stat-leaders-3v3',
    component: StatLeadersView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats/stat-leaders/2v2',
    name: 'stat-leaders-2v2',
    component: StatLeadersView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats/stat-leaders/1v1',
    name: 'stat-leaders-1v1',
    component: StatLeadersView,
    meta: { requiresAuth: true }
  },

  {
    path: '/stats/fraud-watch',
    name: 'fraud-watch',
    component: FraudWatchView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats/h2h',
    name: 'h2h',
    component: H2HView,
    meta: { requiresAuth: true }
  },

  {
    path: '/stats/player/:playerId',
    name: 'player-stats',
    component: PlayerStatsView,
    meta: { requiresAuth: true }
  },

  // Role-restricted routes
  {
    path: '/stats/aliases',
    name: 'alias-manager',
    component: AliasManagerView,
    meta: { requiresAuth: true, roles: ['admin'] }
  },

  {
    path: '/admin',
    name: 'admin',
    component: AdminView,
    meta: { requiresAuth: true, roles: ['admin'] }
  },

  // Redirects from old routes
  {
    path: '/dashboard',
    redirect: '/library'
  },
  {
    path: '/galleries',
    redirect: '/library'
  },
  {
    path: '/analyze/:matchId',
    redirect: to => `/match/${to.params.matchId}`
  },

  // Catch-all redirect
  {
    path: '/:pathMatch(.*)*',
    redirect: '/CvCJbECg53A'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Wait for auth to initialize
  if (!authStore.initialized) {
    await authStore.fetchUser()
  }

  const isPublic = to.meta.public === true
  const requiresAuth = to.meta.requiresAuth === true
  const allowedRoles = to.meta.roles as string[] | undefined

  // Public routes - allow access
  if (isPublic) {
    // If user is logged in and trying to access login, redirect to library
    if (to.name === 'login' && authStore.isAuthenticated) {
      return next({ name: 'library' })
    }
    return next()
  }

  // Protected routes - check auth
  if (requiresAuth && !authStore.isAuthenticated) {
    return next({ name: 'login' })
  }

  // Role-restricted routes - check role
  if (allowedRoles && authStore.user) {
    if (!allowedRoles.includes(authStore.user.role)) {
      return next({ name: 'library' })
    }
  }

  next()
})

export default router
